import React, { useCallback, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button, FileFormatIcon, ScrollArea, Text } from '@/components/ui';
import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconRefresh,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { formatFileSize } from '@/utils/fileUtils';
import { Card, Flex, Hint, ListItem, Progress, Spinner } from '../ui';
import clsx from 'clsx';

const VIRTUALIZATION_THRESHOLD = 50;
const ROW_ESTIMATE_HEIGHT = 48;

export type FileOperationStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'cancelled'
  | 'error';

export interface FileProgressItem {
  id: string | number;
  name: string;
  size?: number;
  status: FileOperationStatus;
  progress: number; // 0-100
  error?: string;
}

export interface FileProgressListProps {
  files: FileProgressItem[];
  currentProcessingIndex?: number;
  isProcessing?: boolean;
  maxHeight?: string;
  onRetryFile?: (fileId: FileProgressItem['id']) => void;
  onRemoveFile?: (fileId: FileProgressItem['id']) => void;
}

const getStatusIcon = (status: FileOperationStatus) => {
  const iconClass = `file-progress-list__icon file-progress-list__icon--${status}`;
  switch (status) {
    case 'completed':
      return <IconCheck className={iconClass} />;
    case 'processing':
      return <Spinner size="1" className={`${iconClass} animate-spin`} />;
    case 'cancelled':
      return <IconX className={iconClass} />;
    case 'error':
      return <IconX className={iconClass} />;
    case 'pending':
    default:
      return <IconClock className={iconClass} />;
  }
};

const getStatusClass = (status: FileOperationStatus): string => {
  return `file-progress-list__item--${status}`;
};

const FileProgressListItem: React.FC<{
  file: FileProgressItem;
  index: number;
  isCurrentProcessing: boolean;
  isHighlighted: boolean;
  isProcessing: boolean;
  onRetryFile?: (fileId: FileProgressItem['id']) => void;
  onRemoveFile?: (fileId: FileProgressItem['id']) => void;
}> = React.memo(
  ({ file, index, isHighlighted, isProcessing, onRetryFile, onRemoveFile }) => {
    const itemClass = `file-progress-list__item ${getStatusClass(file.status)}  ${isHighlighted ? 'file-progress-list__item--highlighted' : ''}`;

    const fileNameClass = `file-progress-list__file-name ${
      isHighlighted ? 'file-progress-list__file-name--highlighted' : ''
    } ${
      file.status === 'error'
        ? 'file-progress-list__file-name--error'
        : file.status === 'completed'
          ? 'file-progress-list__file-name--completed'
          : ''
    } `;

    return (
      <ListItem
        as="div"
        data-file-index={index}
        className={clsx('file-progress-list__file-info', itemClass)}
        startIcon={<FileFormatIcon fileName={file.name} size="2xsmall" />}
        primary={
          <Text
            hightContrast
            size={'2xsmall'}
            weight={isHighlighted ? 'medium' : 'regular'}
            className={fileNameClass}
          >
            {file.name}
          </Text>
        }
        secondary={
          file.error ? (
            <Hint
              hasError
              icon={<IconAlertCircle />}
              className={`file-progress-list__error-message`}
            >
              {file.error}
            </Hint>
          ) : undefined
        }
        endIcon={
          <Flex className="file-progress-list__file-meta">
            {file.status === 'error' && onRetryFile && (
              <Button
                size="xsmall"
                variant="text"
                isIconOnly
                color="secondary"
                onClick={() => onRetryFile(file.id)}
                disabled={isProcessing}
                title="Retry upload"
              >
                <IconRefresh />
              </Button>
            )}
            {isProcessing && (
              <Text
                size="3xsmall"
                weight="medium"
                color="gray"
                className="file-progress-list__progress-percent"
              >
                {file.progress}%
              </Text>
            )}
            {file.size !== undefined && (
              <Text size="3xsmall" className={`file-progress-list__file-size `}>
                {formatFileSize(file.size)}
              </Text>
            )}
            {getStatusIcon(file.status)}
            {onRemoveFile && (
              <Button
                size="xsmall"
                variant="text"
                isIconOnly
                color="error"
                onClick={() => onRemoveFile(file.id)}
                disabled={file.status === 'processing'}
                title={
                  file.status === 'processing'
                    ? 'Cannot remove while uploading'
                    : 'Remove from queue'
                }
              >
                <IconTrash />
              </Button>
            )}
          </Flex>
        }
      >
        {isProcessing && (
          <Progress
            value={file.progress}
            size="xsmall"
            className="file-progress-list__progress-bar"
          />
        )}
      </ListItem>
    );
  }
);

FileProgressListItem.displayName = 'FileProgressListItem';

export const FileProgressList: React.FC<FileProgressListProps> = ({
  files,
  currentProcessingIndex,
  isProcessing = false,
  maxHeight = '300px',
  onRetryFile,
  onRemoveFile,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldVirtualize = files.length > VIRTUALIZATION_THRESHOLD;

  const rowVirtualizer = useVirtualizer({
    count: files.length,
    getScrollElement: useCallback(() => {
      if (!scrollContainerRef.current) return null;
      const viewport = scrollContainerRef.current.querySelector(
        '.rt-ScrollAreaViewport'
      ) as HTMLElement | null;
      return viewport ?? scrollContainerRef.current;
    }, []),
    estimateSize: () => ROW_ESTIMATE_HEIGHT,
    overscan: 5,
    enabled: shouldVirtualize,
  });

  // Auto-scroll to current processing file
  useEffect(() => {
    if (
      !isProcessing ||
      currentProcessingIndex === undefined ||
      currentProcessingIndex < 0 ||
      currentProcessingIndex >= files.length
    ) {
      return;
    }
    if (shouldVirtualize) {
      rowVirtualizer.scrollToIndex(currentProcessingIndex, {
        align: 'center',
        behavior: 'smooth',
      });
    } else if (scrollContainerRef.current) {
      const el = scrollContainerRef.current.querySelector(
        `[data-file-index="${currentProcessingIndex}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [
    isProcessing,
    currentProcessingIndex,
    files.length,
    shouldVirtualize,
    rowVirtualizer,
  ]);

  if (files.length === 0) {
    return null;
  }

  const renderItem = (file: FileProgressItem, index: number) => {
    const isCurrentProcessing =
      currentProcessingIndex === index && file.status === 'processing';
    const isHighlighted = isCurrentProcessing || file.status === 'processing';

    return (
      <FileProgressListItem
        key={file.id}
        file={file}
        index={index}
        isCurrentProcessing={isCurrentProcessing}
        isHighlighted={isHighlighted}
        isProcessing={isProcessing}
        onRetryFile={onRetryFile}
        onRemoveFile={onRemoveFile}
      />
    );
  };

  if (shouldVirtualize) {
    const virtualItems = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();

    return (
      <div ref={scrollContainerRef} style={{ width: '100%' }}>
        <Card size="none" variant="surface" asChild>
          <ScrollArea
            style={{ maxHeight }}
            scrollbars="vertical"
            className="file-progress-list"
          >
            <div
              style={{
                height: totalSize,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualItems.map(virtualRow => {
                const file = files[virtualRow.index];
                if (!file) return null;
                return (
                  <div
                    key={file.id}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: virtualRow.size,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {renderItem(file, virtualRow.index)}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </Card>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} style={{ width: '100%' }}>
      <Card size="none" variant="surface" asChild>
        <ScrollArea
          style={{ maxHeight }}
          scrollbars="vertical"
          className="file-progress-list"
        >
          {files.map((file, index) => (
            <div key={file.id}>{renderItem(file, index)}</div>
          ))}
        </ScrollArea>
      </Card>
    </div>
  );
};
